import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

const VideoPlayerPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [video, setVideo] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [likeCount, setLikeCount] = useState(0);
    const [hasLiked, setHasLiked] = useState(false);
    const [isBookmarked, setIsBookmarked] = useState(false);
    const [commentList, setCommentList] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [relatedArticles, setRelatedArticles] = useState([]);
    const [relatedVideos, setRelatedVideos] = useState([]);
    const [upcomingCourse, setUpcomingCourse] = useState(null);
    const [user, setUser] = useState(null);
    
    // Debug: Log the id
    console.log('VideoPlayerPage - id:', id);
    
    const getAuthInfo = () => {
        const token = localStorage.getItem('app_token');
        if (!token) return { isLoggedIn: false, userId: null, token: null, decoded: null };
        try {
            const decoded = jwtDecode(token);
            return { isLoggedIn: true, userId: decoded.googleId, token, decoded };
        } catch (error) {
            console.error("Invalid token:", error);
            return { isLoggedIn: false, userId: null, token: null, decoded: null };
        }
    };

    useEffect(() => {
        // Don't fetch if id is undefined
        if (!id) {
            console.error('VideoPlayerPage - id is undefined');
            setError("Video ID is missing");
            setIsLoading(false);
            return;
        }

        const fetchData = async () => {
            setIsLoading(true);
            const { isLoggedIn, token, decoded } = getAuthInfo();

            // Set user state if logged in
            if (isLoggedIn && decoded) {
                setUser(decoded);
            }

            try {
                const videoRes = await axios.get(`/api/videos/${id}`);
                setVideo(videoRes.data);
                setLikeCount(videoRes.data.likes || 0);

                const commentsRes = await axios.get(`/api/videos/${id}/comments`);
                setCommentList(commentsRes.data);

                if (isLoggedIn) {
                    const bookmarkStatusRes = await axios.get(
                        `/api/bookmarks/status/video/${id}`,
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    setIsBookmarked(bookmarkStatusRes.data.isBookmarked);
                }

                if (videoRes.data.category) {
                    try {
                        const articlesRes = await axios.get(`/api/articles/summary_list_cat/${videoRes.data.category}`);
                        console.log('Articles API response:', articlesRes.data);
                        // Handle different response structures
                        const articlesData = articlesRes.data.items || articlesRes.data;
                        const articlesArray = Array.isArray(articlesData) ? articlesData : [];
                        setRelatedArticles(articlesArray.slice(0, 2));

                        const videosRes = await axios.get(`/api/videos/summary_list_cat/${videoRes.data.category}`);
                        console.log('Videos API response:', videosRes.data);
                        // Handle different response structures
                        const videosData = videosRes.data.items || videosRes.data;
                        const videosArray = Array.isArray(videosData) ? videosData : [];
                        setRelatedVideos(videosArray.filter(v => v.id !== parseInt(id, 10)).slice(0, 2));
                    } catch (relatedError) {
                        console.error("Failed to fetch related content:", relatedError);
                        setRelatedArticles([]);
                        setRelatedVideos([]);
                    }
                }
            } catch (err) {
                setError("Failed to load video data.");
                console.error("Fetch data error:", err);
            } finally {
                setIsLoading(false);
            }
        };

        const fetchCourses = async () => {
            try {
                const response = await axios.get('/api/courses');
                const liveCourse = response.data.find(course => course.start_date);
                if (liveCourse) {
                    setUpcomingCourse(liveCourse);
                }
            } catch (error) {
                console.error("Failed to fetch courses:", error);
            }
        };

        fetchData();
        fetchCourses();
    }, [id]);

    const handleBookmarkToggle = async () => {
        const { isLoggedIn, token } = getAuthInfo();
        if (!isLoggedIn) {
            navigate('/login');
            return;
        }
        setIsBookmarked(prev => !prev);
        try {
            await axios.post('/api/bookmarks/toggle', 
                { contentId: id, contentType: 'video' },
                { headers: { Authorization: `Bearer ${token}` } }
            );
        } catch (error) {
            console.error("Failed to toggle bookmark", error);
            setIsBookmarked(prev => !prev);
            alert("Failed to update bookmark.");
        }
    };

    const handleLike = async () => {
        const { isLoggedIn, token } = getAuthInfo();
        if (!isLoggedIn) {
            navigate('/login');
            return;
        }
        try {
            const response = await axios.post(
                `/api/videos/${id}/like`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const { liked } = response.data;
            setHasLiked(liked);
            setLikeCount(prevCount => liked ? prevCount + 1 : prevCount - 1);
        } catch (error) {
            console.error("Failed to process like:", error);
            alert("Could not process your like. Please try again.");
        }
    };

    const handleCommentSubmit = async (e) => {
        e.preventDefault();
        const { isLoggedIn, token, decoded } = getAuthInfo();

        if (!isLoggedIn) {
            navigate('/login');
            return;
        }
        if (!newComment.trim()) {
            return;
        }
        try {
            const response = await axios.post(
                `/api/videos/${id}/comments`,
                { commentText: newComment },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const newlyCreatedComment = {
                id: response.data.id,
                comment_text: newComment,
                name: decoded.name,
                profile_picture: decoded.profilePicture,
            };
            setCommentList([newlyCreatedComment, ...commentList]);
            setNewComment('');
        } catch (error) {
            console.error("Failed to post comment:", error);
            alert("Could not post your comment. Please try again.");
        }
    };

    const handleShare = async () => {
        const shareData = {
            title: video.title,
            text: `Check out this video: ${video.title}`,
            url: window.location.href
        };
        if (navigator.share) {
            try {
                await navigator.share(shareData);
            } catch (error) {
                console.error('Error sharing:', error);
            }
        } else {
            try {
                await navigator.clipboard.writeText(window.location.href);
                alert('Link copied to clipboard!');
            } catch (error) {
                console.error('Failed to copy link:', error);
                alert('Could not copy link to clipboard.');
            }
        }
    };

    if (isLoading || !video) return <p>Loading...</p>;

    if (error) return <p>Error: {error}</p>;

    return (
        <>
            <div className="video-player-wrapper">
                <video src={video.video_url} className="video-element" controls autoPlay>
                    Your browser does not support the video tag.
                </video>
            </div>
            <section className="interaction-bar">
                <button onClick={handleLike} className={`interaction-button ${hasLiked ? 'active' : ''}`}>
                    <span className="material-icons-outlined">thumb_up</span>
                    <span>{likeCount}</span>
                </button>
                <button onClick={handleBookmarkToggle} className="interaction-button">
                    <span className={`material-icons-outlined ${isBookmarked ? 'bookmarked' : ''}`}>
                        {isBookmarked ? 'bookmark' : 'bookmark_border'}
                    </span>
                    <span>{isBookmarked ? 'Saved' : 'Save'}</span>
                </button>
                <button onClick={handleShare} className="interaction-button">
                    <span className="material-icons-outlined">share</span>
                    <span>Share</span>
                </button>
            </section>
            <section className="comments-section">
                <h3 className="section-title">Comments ({commentList.length})</h3>
                <form onSubmit={handleCommentSubmit} className="comment-form">
                    <input type="text" placeholder="Add a comment..." className="comment-input" value={newComment} onChange={(e) => setNewComment(e.target.value)} />
                    <button type="submit" className="post-comment-button">Post</button>
                </form>
                <div className="comments-list">
                    {commentList.map(comment => (
                        <div key={comment.id} className="comment-item">
                            <img src={comment.profile_picture || 'https://placehold.co/40x40/16a085/ffffff?text=U'} alt={comment.name} className="comment-author-pic" />
                            <div className="comment-body">
                                <Link to={`/profile/${comment.user_id}`} className="comment-author-name" style={{textDecoration: 'none'}}>
                                    {comment.name}
                                </Link>
                                <p className="comment-text">{comment.comment_text}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
            <div className="related-content-container">
                {upcomingCourse && user && user.role === 'admin' && (
                    <section className="related-section">
                        <h3 className="section-title">Upcoming Course</h3>
                        <div className="upcoming-course-card">
                            <div className="course-card-text">
                                <h4>{upcomingCourse.title}</h4>
                                <p>A new live workshop is coming soon. Stay tuned!</p>
                                <Link to="/courses" className="course-preview-button">
                                    <span className="material-icons">event</span>
                                    <span>View Courses</span>
                                </Link>
                            </div>
                            <img src={upcomingCourse.thumbnail} alt={upcomingCourse.title} className="course-card-image" />
                        </div>
                    </section>
                )}
                <section className="related-section">
                    <h3 className="section-title">Related Videos</h3>
                    <div className="content-list">
                        {relatedVideos.map(item => (
                            <Link to={`/videos/${item.id}`} key={item.id} className="content-card">
                                <img src={item.thumbnail_url} alt={item.title} className="content-card-thumbnail" />
                                <div className="content-card-body">
                                    <h3 className="content-card-title">{item.title}</h3>
                                    <p className="content-card-description">{item.category}</p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
                <section className="related-section">
                    <h3 className="section-title">Related Articles</h3>
                    <div className="content-list">
                        {relatedArticles.map(item => (
                            <Link to={`/articles/${item.id}`} key={item.id} className="content-card">
                                <img src={item.banner_image_url} alt={item.title} className="content-card-thumbnail" />
                                <div className="content-card-body">
                                    <h3 className="content-card-title">{item.title}</h3>
                                    <p className="content-card-description">{item.category}</p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            </div>
        </>
    );
};
export default VideoPlayerPage;
